import { defineStore } from "pinia";
import api from "@/api";

export const useReportsStore = defineStore("reports", () => {
	const sectors = ref<Record<string, any>[]>([]);
	const counties = ref<any>([]);
	const previewData = ref<any>({});
	const pagination = ref<Record<string, any>>({
		totalPages: 0,
		currentPage: 0,
		currentPageData: {},
	});
	const reports = ref<Record<string, any>[]>([]);
	const recalculationScenario = ref([
		//in the future, it may be obtained from the backend
		{
			label: "Pay and then Bill",
			sticker: "P&B",
			order: 0,
			code: "PYBL",
		},
		{
			label: "Bill and then Pay",
			sticker: "B&P",
			order: 1,
			code: "BLPY",
		},
		{
			label: "Only Bill",
			sticker: "B",
			order: 2,
			code: "ONBL",
		},
		{
			label: "Only Pay",
			sticker: "P",
			order: 3,
			code: "ONPY",
		},
	]);

	// helpers
	function tryToJsonParseBinaryResponseAndGetProperty(
		response: any,
		key: string,
	) {
		try {
			// handle arraybuffer response type from Axios library that is set for report APIs
			const responseString = new TextDecoder("utf8").decode(response.data);
			const responseJson = JSON.parse(responseString);
			return responseJson[key];
		} catch {}
		return null;
	}

	// actions

	async function waitForBackgroundRequest(
		background_request_id: string | null | undefined,
		{
			responseType,
		}: {
			responseType: "json" | "arraybuffer" | null | undefined;
		},
	) {
		let response: any = null;
		while (background_request_id) {
			// start calling check API
			// if check API keeps returning "response?.data._background_request_id" then keep calling next check API
			// stop calling when "response?.data._background_request_id" is not defined, if that is the case -> assume that background request has finished
			// expect Blob data for all downloaded reports and json for preview, when background job finishes

			response = await api.getCenBackgroundRequestUpdate({
				background_request_id,
			});

			const check_background_request_id =
				response.data._background_request_id || null;
			const terminate_request = response.data._terminate_request || false;

			if (terminate_request === true) {
				return null;
			}
			if (check_background_request_id) {
				background_request_id = check_background_request_id;
				// wait for 5 seconds, then call api for checking background request process again
				await new Promise((resolve, reject) => {
					setTimeout(() => {
						resolve(true);
					}, 5000);
				});
			} else {
				// when "background_request_id" is null, it means that background job has finished and we can call for the response
				responseType = responseType || "arraybuffer";
				response = await api.getCenBackgroundResponseFromRequest(
					{
						background_request_id,
					},
					{ responseType: responseType },
				);
				return response;
			}
		}
		return null;
	}

	function fetchPageHasChanged(payload) {
		pagination.value.currentPage = payload;
		pagination.value.currentPageData =
			previewData.value?.data.pages[payload - 1];
	}

	async function fetchReportList(params?: any) {
		try {
			const res = await api.fetchReportList(params);
			reports.value = res.data.data;
			// commit("STORE_REPORTS", res.data);
		} catch (err) {
			console.log("Failed to fetch reports", err.message);
		}
	}

	async function fetchPreviewReports(params) {
		resetPreviewData();
		try {
			let response = await api.fetchReports(params);

			// if response has "background_request_id" parameter, then start calling "waitForBackgroundRequest"
			// this means that slow/complex API has been called and the logic is working in the server background
			const background_request_id: string | null | undefined =
				response?.data?._background_request_id;

			if (background_request_id) {
				response = await waitForBackgroundRequest(background_request_id, {
					responseType: "json",
				});
			}

			previewData.value = response.data;
			if (response.data.data.pages !== undefined) {
				// commit("STORE_PREVIEW_DATA", response.data);
				previewData.value = response.data;
				// commit("STORE_PAGINATION", response.data);
				const total = response.data.data.pages.length;
				pagination.value.totalPages = total;
				if (total !== 0) {
					pagination.value.currentPage = 1;
					pagination.value.currentPageData = response.data.data.pages[0];
				}
				return;
			}
		} catch (e) {
			// return Promise.reject();
			throw e;
		}
	}
	function resetPreviewData() {
		previewData.value = {};
		pagination.value.totalPages = 0;
		pagination.value.currentPage = 0;
		pagination.value.currentPageData = {};
	}

	// START get sectors
	function getSectors() {
		// Replacing reduce with forEach for traversing subsectors
		const traverseSubs = (sectors) => {
			const result = [];
			for (const curr of sectors) {
				if (curr.selectable) {
					// Add selectable sector to the result

					result.push(curr);
				} else if (curr.subsectors) {
					// Recursively traverse subsectors
					const subSec = traverseSubs(curr.subsectors);
					result.push(...subSec);
				}
			}
			return result;
		};

		// Replacing reduce with forEach for creating sector parents
		const createSectorParents = (subs) => {
			const result = [];
			for (const curr of subs) {
				// Add parent sector names without duplicates
				if (!result.includes(curr.sectorParent.name)) {
					result.push(curr.sectorParent.name);
				}
			}
			return result;
		};

		// Traversing the state's sectors
		const subs = traverseSubs(sectors.value);
		// Creating a list of sector parent names
		const parentSubs = createSectorParents(subs);

		// Generating subs grouped by parent names
		const getSubsWithParent = (parents) => {
			const allSubs = parents.map((parentName) => {
				// Collect all subs for a given parent name
				const genSubs = subs.filter(
					(sub) => sub.sectorParent.name === parentName,
				);
				return {
					name: parentName,
					parentName: parentName,
					options: genSubs,
				};
			});
			return allSubs;
		};

		// Returning the final result
		return getSubsWithParent(parentSubs);
	}
	// END get sectors

	async function fetchSectors(q: any = {}) {
		// includes: ""
		try {
			const res = await api.fetchSectors(q);
			sectors.value = res.data.data;
		} catch (e) {
			console.log(e, "LCM: Something went wrong while fetching sectors.");
		}
		// return api
		//   .fetchSectors(q)
		//   .then((response) => {
		// 	commit("STORE_SECTORS", response.data);
		//   })
		//   .catch(() =>
		// 	console.log("LCM: Something went wrong while fetching sectors.")
		//   );
	}

	return {
		sectors,
		counties,
		previewData,
		pagination,
		reports,
		recalculationScenario,
		//
		tryToJsonParseBinaryResponseAndGetProperty,
		//
		waitForBackgroundRequest,
		fetchPageHasChanged,
		fetchReportList,
		fetchPreviewReports,
		getSectors,
		fetchSectors,
		resetPreviewData,
	};
});
